/**
 * components/landing/platform/sections/AIEngine.jsx — §3.8.
 *
 * How AI actually works in Kalki Ai Cloud.  Left column: capability grid.
 * Right column: a small live-ish depiction of the RAG pipeline going
 * from question → KB retrieval → grounded answer.
 */

import React from 'react'
import clsx from 'clsx'
import {
  Bot, BookOpen, Wand2, Sparkles, Workflow, Compass, Plug, ArrowRight,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading } from '@/components/landing/design-system'
import { AI_CAPS } from '../data'

const ICONS = { Bot, BookOpen, Wand2, Sparkles, Workflow, Compass, Plug }
const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export default function AIEngine() {
  return (
    <section className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.35]"
        style={{ background: 'radial-gradient(50% 40% at 50% 20%, rgba(168,85,247,0.10), transparent 70%)' }} />
      <Container size="wide" className="flex flex-col gap-12">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>AI &amp; Automation Engine</Eyebrow>
          <SectionHeading
            align="center"
            title={<>Intelligence — <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">not marketing.</span></>}
            subtitle="Kalki Ai Cloud’s AI isn’t a badge — it’s a deeply-integrated layer that reads the same data your operators do, cites its sources, and can act on your behalf when you grant it a tool."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10">
          {/* Capabilities grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {AI_CAPS.map((c) => {
              const tone = TONE[c.tone] || TONE.orange
              const Icon = ICONS[c.iconKey] || Sparkles
              return (
                <div key={c.id} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 hover:bg-white/[0.03] transition-all">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border"
                      style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                      <Icon style={{ width: 14, height: 14 }} strokeWidth={1.8} />
                    </span>
                    <h4 className="text-[14.5px] font-semibold text-white leading-tight">{c.label}</h4>
                  </div>
                  <p className="text-[13px] text-zinc-400 leading-relaxed mt-2">{c.body}</p>
                </div>
              )
            })}
          </div>

          {/* Live-ish RAG pipeline visualization */}
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden relative">
            <div className="pointer-events-none absolute inset-0 opacity-70"
              style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(168,85,247,0.14), transparent 70%)' }} />
            <div className="relative flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-brand-gold" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-gold">RAG pipeline · anatomy</span>
              </div>
              <h4 className="text-lg font-semibold text-white leading-tight">From question to grounded answer.</h4>

              {/* Pipeline steps */}
              <div className="grid grid-cols-1 gap-2 text-[12.5px]">
                <PipelineStep n={1} tone="orange" title="Operator asks a question" body='“Why is my MES strategy underperforming this week?”' meta="context: module=strategy, ticker=MES" />
                <PipelineArrow />
                <PipelineStep n={2} tone="purple" title="Retriever pulls context" body="Semantic search over strategy playbooks, recent retros, current portfolio metrics." meta="top-k = 6 · role-scoped" />
                <PipelineArrow />
                <PipelineStep n={3} tone="blue"   title="Prompt orchestrator composes" body="System prompt + tool schema + retrieved context + user query — versioned." meta="template: mentor-v14" />
                <PipelineArrow />
                <PipelineStep n={4} tone="green"  title="Model reasons + cites" body="Regime shifted low-vol range-bound Wed — win-rate 58% → 41%. 2 citations, 1 chart, 1 action." meta="model · gpt-4o · grounded=true" />
              </div>

              <div className="mt-2 rounded-lg border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-2 text-[12.5px] text-emerald-200 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-emerald-300" />
                Every answer includes citations — no hallucinations, no anonymous claims.
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

function PipelineStep({ n, tone, title, body, meta }) {
  const t = TONE[tone]
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <span
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-mono font-bold"
        style={{ borderColor: `rgba(${t.rgb},0.35)`, background: `rgba(${t.rgb},0.08)`, color: t.hex, border: `1px solid rgba(${t.rgb},0.35)` }}
      >{n}</span>
      <div className="flex-1 min-w-0">
        <div className="text-white font-semibold text-[13px] leading-tight">{title}</div>
        <div className="text-zinc-300 text-[12.5px] mt-0.5 leading-relaxed">{body}</div>
        <div className="text-[10px] font-mono text-zinc-500 mt-1">{meta}</div>
      </div>
    </div>
  )
}

function PipelineArrow() {
  return (
    <div className="flex justify-center">
      <ArrowRight className="h-3 w-3 text-zinc-600 rotate-90" />
    </div>
  )
}
